export type ScheduleEntry = {
  id: string;
  job_id: string | null;
  kind: "job" | "hold";
  customer: string | null;
  vehicle?: {
    make: string;
    model: string;
    registration_number?: string | null;
  };
  bay_id: string;
  bay_name: string;
  starts_at: string;
  ends_at: string;
  time_zone: string;
  occupied_until?: string | null;
  occupied_from?: string | null;
  open_ended?: boolean;
  period: "past" | "current" | "future";
  state: string;
  work_state?: string;
  hold_expires_at?: string;
  parent?: { id: string; kind: "job" | "request" } | null;
};
export type ScheduleDay = { date: string; starts_at: string; ends_at: string };
export type ScheduleSegment = {
  entry: ScheduleEntry;
  day: string;
  startMinute: number;
  endMinute: number;
};

export function scheduleHour(value: string, zone: string) {
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZoneName: "longOffset",
  }).formatToParts(new Date(value));
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)!.value;
  const label = `${get("hour")}:${get("minute")}`;
  const offset = get("timeZoneName").replace(/^GMT/, "") || "+00:00";
  return {
    label,
    offset,
    iso: `${get("year")}-${get("month")}-${get("day")}T${label}:00${offset}`,
  };
}
export function localDate(value: string, zone: string) {
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(value));
}
export function addDays(day: string, count: number) {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + count);
  return date.toISOString().slice(0, 10);
}
function minutes(value: string, zone: string) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: zone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(value));
  return (
    Number(parts.find((part) => part.type === "hour")!.value) * 60 +
    Number(parts.find((part) => part.type === "minute")!.value)
  );
}
export function scheduleSegments(
  entries: ScheduleEntry[],
  from: string,
  through: string,
  zone: string,
  days?: ScheduleDay[],
): ScheduleSegment[] {
  return entries.flatMap((entry) => {
    const end =
      entry.occupied_until &&
      new Date(entry.occupied_until) > new Date(entry.ends_at)
        ? entry.occupied_until
        : entry.ends_at;
    const start = entry.occupied_from || entry.starts_at;
    if (days)
      return days.flatMap((day) => {
        const left = Math.max(
          new Date(start).getTime(),
          new Date(day.starts_at).getTime(),
        );
        const right = Math.min(
          new Date(end).getTime(),
          new Date(day.ends_at).getTime(),
        );
        return right > left
          ? [
              {
                entry,
                day: day.date,
                startMinute: (left - new Date(day.starts_at).getTime()) / 60000,
                endMinute: (right - new Date(day.starts_at).getTime()) / 60000,
              },
            ]
          : [];
      });
    const first = localDate(start, zone),
      last = localDate(end, zone);
    const rows: ScheduleSegment[] = [];
    for (
      let day = first < from ? from : first;
      day <= through && day <= last;
      day = addDays(day, 1)
    ) {
      const startMinute = day === first ? minutes(start, zone) : 0;
      const endMinute = day === last ? minutes(end, zone) : 1440;
      if (endMinute > 0)
        rows.push({
          entry,
          day,
          startMinute,
          endMinute: Math.max(startMinute + 15, endMinute),
        });
    }
    return rows;
  });
}

// The server supplies each local day's absolute bounds, including DST. Preserve
// the exact selected occurrence when converting a slot to datetime-local input.
export function slotValues(day: ScheduleDay, minute: number, zone: string) {
  const start = new Date(day.starts_at).getTime() + minute * 60000;
  const end = Math.min(start + 3600000, new Date(day.ends_at).getTime());
  const wall = (time: number) => {
    const parts = new Intl.DateTimeFormat("sv-SE", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date(time));
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)!.value;
    const value = `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
    const offset = Math.round(
      (new Date(`${value}:00Z`).getTime() - time) / 60000,
    );
    return {
      value,
      offset: `${offset < 0 ? "-" : "+"}${String(Math.floor(Math.abs(offset) / 60)).padStart(2, "0")}:${String(Math.abs(offset) % 60).padStart(2, "0")}`,
    };
  };
  const from = wall(start),
    to = wall(end);
  return {
    starts_at: from.value,
    ends_at: to.value,
    starts_at_offset: from.offset,
    ends_at_offset: to.offset,
  };
}
